/**
 * Unified Authentication Utilities
 * Shared functions for both SSO and Direct Auth
 * Using localStorage for persistent login across browser sessions
 */

import { trackLogin } from "./analytics"
import { readApiErrorMessage } from "./read-api-error-message"
import { TokenRefresh } from "./token-refresh"

async function getResponseErrorMessage(
  response: Response,
  fallback: string
): Promise<string> {
  try {
    const data: unknown = await response.json()
    return readApiErrorMessage(data) ?? fallback
  } catch {
    return fallback
  }
}

/**
 * Unified logout function for all auth methods
 * Clears all tokens and redirects to login
 */
export function logout(): void {
  // Clear all auth tokens from localStorage
  TokenRefresh.clearTokens()
  localStorage.removeItem("pkce_verifier")
  localStorage.removeItem("return_url")
  localStorage.removeItem("challenge_session")
  localStorage.removeItem("challenge_username")
  localStorage.removeItem("user_info") // Clear stored user info
  localStorage.removeItem("remember_me") // Clear remember me flag
  localStorage.removeItem("remember_me_timestamp") // Clear remember me timestamp
  localStorage.removeItem("session_timestamp") // Clear standard session timestamp

  // Use replace so the current page is removed from the browser history stack.
  // This prevents the back button from returning to a protected page after logout.
  window.location.replace("/login")
}

/**
 * Check if user is authenticated (checks both auth methods)
 * Note: This is a synchronous check. Use ensureValidToken() for async checks with refresh
 */
export function isAuthenticated(): boolean {
  if (typeof window === "undefined") return false

  const accessToken = localStorage.getItem("access_token")
  if (!accessToken) {
    return false
  }

  // Basic expiry check (will be refreshed automatically by API calls)
  const expiryTime = localStorage.getItem("token_expiry")
  if (expiryTime && Date.now() > parseInt(expiryTime, 10)) {
    // Token is expired, but don't logout immediately
    // Let the API interceptor handle refresh
    return false
  }

  return true
}

/**
 * Ensure user has valid tokens (with automatic refresh)
 * Use this before critical operations or on route guards
 */
export async function ensureAuthenticated(): Promise<boolean> {
  if (typeof window === "undefined") return false

  const accessToken = localStorage.getItem("access_token")

  if (!accessToken) {
    return false
  }

  // Check and refresh if needed
  const isValid = await TokenRefresh.ensureValidToken()
  return isValid
}

/**
 * Get current user info from ID token (works for both auth methods)
 */
export function getCurrentUser(): { email?: string; sub?: string } | null {
  const idToken = localStorage.getItem("id_token")
  if (!idToken) return null

  try {
    const payload = idToken.split(".")[1]
    const decoded = JSON.parse(atob(payload))
    return {
      email: decoded.email,
      sub: decoded.sub,
    }
  } catch {
    return null
  }
}

/**
 * Verify token by calling auth/me API
 * Should be called after tokens are stored
 * @param loginMethod - Optional: Track successful login with this method ('email' or 'sso')
 */
export async function verifyToken(
  loginMethod?: "email" | "sso"
): Promise<{ success: boolean; error?: string }> {
  const API_BASE_URL =
    process.env.NEXT_PUBLIC_API_URL ||
    "https://streamerhub-be-dev.streamstats.net"

  const idToken = localStorage.getItem("id_token")

  if (!idToken) {
    return {
      success: false,
      error: "No ID token found",
    }
  }

  try {
    const response = await fetch(`${API_BASE_URL}/api/v1/auth/me`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${idToken}`,
        "Content-Type": "application/json",
      },
    })

    if (!response.ok) {
      // Try to parse error message from response body
      const errorMessage = await getResponseErrorMessage(
        response,
        `Token verification failed: ${response.status}`
      )

      // 403 Forbidden means user is not authorized - logout immediately
      if (response.status === 403) {
        logout()
        return {
          success: false,
          error: `403: ${errorMessage}`,
        }
      }
      return {
        success: false,
        error: errorMessage,
      }
    }

    const data = await response.json()

    // Optionally store user info in localStorage
    localStorage.setItem("user_info", JSON.stringify(data))

    // Track successful login if method is provided
    if (loginMethod) {
      try {
        trackLogin(loginMethod)
      } catch {
        // Silently fail if tracking fails
      }
    }

    return {
      success: true,
    }
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error ? error.message : "Token verification failed",
    }
  }
}
